import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

export const ServiceInfogerance = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[700px] -right-20 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-200/15 to-blue-200/15 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="mb-8">
          <Link to="/services" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center mb-4">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Retour aux services
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Infogérance & maintenance au quotidien
          </h1>
          <p className="text-xl text-gray-600">
            J'assure le bon fonctionnement de votre informatique au jour le jour : postes de travail, serveurs, réseau, sauvegardes et mises à jour.
          </p>
        </div>

        {/* Objectif */}
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-lg text-gray-800">
              <strong>L'objectif est simple :</strong> moins de pannes, une infrastructure stable et un interlocuteur unique qui suit votre environnement dans la durée.
            </p>
          </CardContent>
        </Card>

        {/* Situations d'intervention */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Dans quelles situations j'interviens ?</h2>
          <div className="space-y-3">
            {[
              'Surveillance et maintenance préventive de vos serveurs et postes de travail',
              'Gestion des mises à jour de sécurité et des correctifs système',
              'Suivi et vérification régulière des sauvegardes',
              'Gestion des comptes utilisateurs et des accès',
              'Surveillance de la santé du réseau et des équipements',
              'Interventions sur incidents récurrents pour éviter qu\'ils ne se reproduisent',
              'Reporting régulier sur l\'état de votre infrastructure',
              'Conseils sur les évolutions et optimisations possibles'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bénéfices */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Les bénéfices pour votre entreprise</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Moins de pannes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Maintenance préventive et surveillance continue pour éviter les incidents</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Meilleure disponibilité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Vos équipes peuvent travailler sans interruption</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sécurité renforcée</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Mises à jour appliquées régulièrement, sauvegardes vérifiées</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Visibilité et contrôle</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Vous savez toujours où vous en êtes avec des rapports clairs</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Sur mesure */}
        <Card className="mb-8 border-gray-300">
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Prestation sur mesure, sur devis</h3>
            <p className="text-gray-600 mb-4">
              Chaque entreprise a des besoins différents. Je vous propose une infogérance adaptée à votre taille, 
              votre budget et vos enjeux, avec un suivi régulier et une vraie relation de confiance.
            </p>
            <p className="text-gray-600">
              Les modalités (fréquence d'intervention, périmètre, niveau de service) sont définies ensemble pour coller à votre réalité opérationnelle.
            </p>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Parlons de votre besoin</h3>
          <p className="text-gray-600 mb-6">
            Discutons de votre situation et voyons comment je peux vous accompagner au quotidien.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              <a href="https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled" target="_blank" rel="noopener noreferrer">
                <Phone className="w-5 h-5 mr-2" />
                Prendre RDV (30 min)
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              <Link to="/contact">
                <Mail className="w-5 h-5 mr-2" />
                Me contacter
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};